import React from 'react';
import { Card, CardBody, Typography } from '@material-tailwind/react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { FaCalendarAlt } from 'react-icons/fa';
import LoadingPanel from './LoadingPanel';

const MonthlyInvoiceTrendReport = ({
  trendLoading,
  trendYear,
  trendSummary,
  trendChart,
  trendTopOwners,
  trendOwnerMatrix,
  trendMonthRows,
  formatMoney
}) => {
  const trendMonthLookup = trendMonthRows.reduce((acc, row) => {
    acc[Number(row.month_no || 0)] = row;
    return acc;
  }, {});

  const monthlyTotals = trendChart.reduce((acc, row) => {
    acc.invoice_count += Number(row.invoice_count || 0);
    acc.gross_amount += Number(row.gross_amount || 0);
    acc.discount_amount += Number(row.discount_amount || 0);
    acc.net_amount += Number(row.net_amount || 0);
    acc.paid_count += Number(trendMonthLookup[row.month_no]?.paid_count || 0);
    acc.draft_count += Number(trendMonthLookup[row.month_no]?.draft_count || 0);
    acc.sent_count += Number(trendMonthLookup[row.month_no]?.sent_count || 0);
    acc.reversed_count += Number(trendMonthLookup[row.month_no]?.reversed_count || 0);
    return acc;
  }, {
    invoice_count: 0,
    gross_amount: 0,
    discount_amount: 0,
    net_amount: 0,
    paid_count: 0,
    draft_count: 0,
    sent_count: 0,
    reversed_count: 0
  });

  const ownerMatrixTotals = trendChart.reduce((acc, monthRow) => {
    acc[monthRow.month_no] = trendOwnerMatrix.reduce((sum, owner) => sum + Number(owner.months[monthRow.month_no] || 0), 0);
    return acc;
  }, {});

  const ownerMatrixGrandTotal = trendOwnerMatrix.reduce((sum, owner) => sum + Number(owner.total || 0), 0);

  if (trendLoading) {
    return <LoadingPanel label={`Loading monthly trend for ${trendYear}...`} />;
  }

  return (
    <div className="space-y-4">
      <Card className="shadow-lg">
        <CardBody className="space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
                <FaCalendarAlt />
                Monthly Trend
              </div>
              <Typography className="mt-1 text-sm text-slate-500">
                Invoice totals by month for {trendYear}, plus owner-level totals and month-by-month owner metrics.
              </Typography>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            <Card className="border border-slate-200 shadow-none"><CardBody><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Invoices</div><div className="mt-2 text-3xl font-bold text-slate-900">{Number(trendSummary.invoice_count || 0)}</div></CardBody></Card>
            <Card className="border border-slate-200 shadow-none"><CardBody><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Gross</div><div className="mt-2 text-2xl font-bold text-slate-900">{formatMoney(trendSummary.gross_amount || 0)}</div></CardBody></Card>
            <Card className="border border-slate-200 shadow-none"><CardBody><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Net</div><div className="mt-2 text-2xl font-bold text-emerald-700">{formatMoney(trendSummary.net_amount || 0)}</div></CardBody></Card>
            <Card className="border border-slate-200 shadow-none"><CardBody><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Avg / Month</div><div className="mt-2 text-2xl font-bold text-slate-900">{formatMoney(trendSummary.average_monthly_net || 0)}</div></CardBody></Card>
            <Card className="border border-slate-200 shadow-none"><CardBody><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Peak Month</div><div className="mt-2 text-2xl font-bold text-orange-700">{trendSummary.peak_month_label || '-'}</div><div className="mt-1 text-xs text-slate-500">{formatMoney(trendSummary.peak_month_net || 0)}</div></CardBody></Card>
          </div>
        </CardBody>
      </Card>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card className="shadow-lg">
          <CardBody className="p-0">
            <div className="border-b border-slate-200 px-5 py-4">
              <div className="text-sm font-semibold uppercase tracking-wide text-slate-500">Monthly invoice totals</div>
            </div>
            <div className="h-[340px] px-3 py-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trendChart} margin={{ top: 5, right: 12, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month_label" />
                  <YAxis />
                  <Tooltip formatter={(value, name) => (name === 'net_amount' ? formatMoney(value) : value)} />
                  <Bar dataKey="net_amount" fill="#f97316" radius={[10, 10, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardBody>
        </Card>

        <Card className="shadow-lg">
          <CardBody className="p-0">
            <div className="border-b border-slate-200 px-5 py-4">
              <div className="text-sm font-semibold uppercase tracking-wide text-slate-500">Top owners by annual net</div>
            </div>
            <div className="h-[340px] px-3 py-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trendTopOwners} layout="vertical" margin={{ top: 5, right: 20, left: 24, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" />
                  <YAxis dataKey="owner_name" type="category" width={140} />
                  <Tooltip
                    formatter={(value) => [formatMoney(value), 'Net Amount']}
                    labelFormatter={(_, payload) => {
                      const item = payload?.[0]?.payload;
                      if (!item) {
                        return '';
                      }

                      return item.owner_name ? `${item.owner_name} (${item.owner_code})` : item.owner_code;
                    }}
                  />
                  <Bar dataKey="net_amount" name="Net Amount" fill="#0ea5e9" radius={[0, 10, 10, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardBody>
        </Card>
      </div>

      <div className="space-y-4">
        <Card className="shadow-lg">
          <CardBody className="p-0">
            <div className="border-b border-slate-200 px-5 py-4">
              <div className="text-sm font-semibold uppercase tracking-wide text-slate-500">Monthly totals table</div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-[980px] w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Month</th>
                    <th className="px-4 py-3 text-right font-semibold">Invoices</th>
                    <th className="px-4 py-3 text-right font-semibold">Gross</th>
                    <th className="px-4 py-3 text-right font-semibold">Discount</th>
                    <th className="px-4 py-3 text-right font-semibold">Net</th>
                    <th className="px-4 py-3 text-right font-semibold">Paid</th>
                    <th className="px-4 py-3 text-right font-semibold">Draft</th>
                    <th className="px-4 py-3 text-right font-semibold">Sent</th>
                    <th className="px-4 py-3 text-right font-semibold">Reversed</th>
                  </tr>
                </thead>
                <tbody>
                  {trendChart.map((row) => (
                    <tr key={row.month_no} className="border-t border-slate-200">
                      <td className="px-4 py-3 font-semibold text-slate-900">{row.month_label}</td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-900">{row.invoice_count}</td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-900">{formatMoney(row.gross_amount)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-900">{formatMoney(row.discount_amount)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-emerald-700">{formatMoney(row.net_amount)}</td>
                      <td className="px-4 py-3 text-right text-slate-700">{Number(trendMonthLookup[row.month_no]?.paid_count || 0)}</td>
                      <td className="px-4 py-3 text-right text-slate-700">{Number(trendMonthLookup[row.month_no]?.draft_count || 0)}</td>
                      <td className="px-4 py-3 text-right text-slate-700">{Number(trendMonthLookup[row.month_no]?.sent_count || 0)}</td>
                      <td className="px-4 py-3 text-right text-slate-700">{Number(trendMonthLookup[row.month_no]?.reversed_count || 0)}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-slate-300 bg-slate-50">
                    <td className="px-4 py-3 font-semibold text-slate-900">Total</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">{monthlyTotals.invoice_count}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">{formatMoney(monthlyTotals.gross_amount)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">{formatMoney(monthlyTotals.discount_amount)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-emerald-700">{formatMoney(monthlyTotals.net_amount)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-700">{monthlyTotals.paid_count}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-700">{monthlyTotals.draft_count}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-700">{monthlyTotals.sent_count}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-700">{monthlyTotals.reversed_count}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>

        <Card className="shadow-lg">
          <CardBody className="p-0">
            <div className="border-b border-slate-200 px-5 py-4">
              <div className="text-sm font-semibold uppercase tracking-wide text-slate-500">Owner-by-month matrix</div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-[1200px] w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="sticky left-0 z-10 bg-slate-50 px-4 py-3 text-left font-semibold">Owner</th>
                    {trendChart.map((monthRow) => (
                      <th key={monthRow.month_no} className="px-3 py-3 text-right font-semibold">{monthRow.month_label}</th>
                    ))}
                    <th className="px-4 py-3 text-right font-semibold">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {trendOwnerMatrix.length > 0 ? trendOwnerMatrix.map((owner) => (
                    <tr key={owner.owner_code} className="border-t border-slate-200">
                      <td className="sticky left-0 z-10 bg-white px-4 py-3">
                        <div className="font-semibold text-slate-900">{owner.owner_code}</div>
                        <div className="text-[11px] text-slate-500">{owner.owner_name}</div>
                      </td>
                      {trendChart.map((monthRow) => (
                        <td key={`${owner.owner_code}-${monthRow.month_no}`} className="px-3 py-3 text-right text-slate-700">
                          {formatMoney(owner.months[monthRow.month_no] || 0)}
                        </td>
                      ))}
                      <td className="px-4 py-3 text-right font-semibold text-emerald-700">{formatMoney(owner.total || 0)}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={14} className="px-4 py-8 text-center text-slate-500">No owner trend data found for this year.</td>
                    </tr>
                  )}
                  {trendOwnerMatrix.length > 0 && (
                    <tr className="border-t-2 border-slate-300 bg-slate-50">
                      <td className="sticky left-0 z-10 bg-slate-50 px-4 py-3 font-semibold text-slate-900">Total</td>
                      {trendChart.map((monthRow) => (
                        <td key={`total-${monthRow.month_no}`} className="px-3 py-3 text-right font-semibold text-slate-900">
                          {formatMoney(ownerMatrixTotals[monthRow.month_no] || 0)}
                        </td>
                      ))}
                      <td className="px-4 py-3 text-right font-semibold text-emerald-700">{formatMoney(ownerMatrixGrandTotal)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default MonthlyInvoiceTrendReport;
